import { computeParcelas, somaParcelas } from "../lib/parcelas";
import { fmtBRL } from "../lib/format";
import { DatePicker } from "./DatePicker";

const base = "bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const label = "text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1";

const pad = (n) => String(n).padStart(2, "0");
export const addDays = (iso, days) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const emptyParc = (n = 3) => ({ total: "", n: String(n), due_dates: Array(n).fill(""), manual: Array(n).fill("") });

export const parcelasPayload = (parc) =>
  computeParcelas(parc.total, parc.manual).map((v, i) => ({ amount: v, due_date: parc.due_dates[i] }));

export const parcelasProblem = (parc) => {
  const vals = computeParcelas(parc.total, parc.manual);
  const total = parseFloat(parc.total) || 0;
  if (total <= 0) return "Informe o valor total";
  if (vals.some((v) => v <= 0)) return "Há parcela com valor zerado ou negativo — ajuste os valores editados";
  if (Math.abs(somaParcelas(vals) - total) > 0.009) return "A soma das parcelas não bate com o valor total";
  const missing = parc.due_dates.findIndex((d) => !d);
  if (missing >= 0) return `Informe o vencimento da parcela ${missing + 1}`;
  return null;
};

export const AvulsoParcelasForm = ({ parc, setParc }) => {
  const vals = computeParcelas(parc.total, parc.manual);
  const total = parseFloat(parc.total) || 0;
  const soma = somaParcelas(vals);
  const ok = Math.abs(soma - total) <= 0.009 && vals.every((v) => v > 0);

  const setTotal = (v) => {
    if (parc.manual.some((m) => m !== "")) {
      if (!window.confirm("Você ajustou valores de parcelas manualmente. Alterar o Valor Total redistribui TODAS as parcelas igualmente e desfaz esses ajustes. Continuar?")) return;
      setParc({ ...parc, total: v, manual: parc.manual.map(() => "") });
      return;
    }
    setParc({ ...parc, total: v });
  };
  const setN = (raw) => {
    const num = parseInt(raw);
    const next = { ...parc, n: raw };
    if (!isNaN(num) && num >= 2 && num <= 36) {
      next.due_dates = Array.from({ length: num }, (_, i) => parc.due_dates[i] || (parc.due_dates[0] ? addDays(parc.due_dates[0], 30 * i) : ""));
      next.manual = Array(num).fill("");
    }
    setParc(next);
  };
  const normalizeN = () => {
    const num = parseInt(parc.n);
    if (isNaN(num) || num < 2) setN("2");
    else if (num > 36) setN("36");
  };
  const setDue = (i, v) => {
    const due_dates = [...parc.due_dates];
    due_dates[i] = v;
    if (i === 0 && v) for (let k = 1; k < due_dates.length; k++) due_dates[k] = addDays(v, 30 * k);
    setParc({ ...parc, due_dates });
  };
  const setManual = (i, v) => setParc({ ...parc, manual: parc.manual.map((m, k) => (k === i ? v : m)) });

  return (
    <div className="space-y-3" data-testid="avulso-parcelas-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Valor Total (R$)</label>
          <input data-testid="avulso-total-input" type="number" step="0.01" min="0.01" required className={`${base} w-full`} value={parc.total} onChange={(e) => setTotal(e.target.value)} />
        </div>
        <div>
          <label className={label}>Quantidade de Parcelas</label>
          <input data-testid="avulso-num-parcelas" type="number" min="2" max="36" required className={`${base} w-full`} value={parc.n} onChange={(e) => setN(e.target.value)} onBlur={normalizeN} />
        </div>
      </div>
      <div data-testid="avulso-parcelas-list" className="border border-zinc-800 rounded p-3 space-y-2.5">
        <p className={label}>Valor e vencimento de cada parcela</p>
        <p className="text-[11px] text-zinc-500">O 1º vencimento define os demais a cada 30 dias — ajuste qualquer data ou valor individualmente. As parcelas não editadas se recalculam para a soma bater com o total.</p>
        {parc.due_dates.map((d, i) => {
          const isManual = parc.manual[i] !== "";
          return (
            <div key={i} className="flex flex-wrap items-center gap-2.5" data-testid={`avulso-parcela-row-${i + 1}`}>
              <span className="text-sm text-zinc-300 w-20 shrink-0">
                Parcela {i + 1}
                {isManual && (
                  <button type="button" data-testid={`avulso-parcela-reset-${i + 1}`} title="Voltar ao cálculo automático" onClick={() => setManual(i, "")}
                    className="block text-[10px] text-amber-400 hover:text-amber-300 underline underline-offset-2">editada · auto</button>
                )}
              </span>
              <input data-testid={`avulso-parcela-valor-${i + 1}`} type="number" step="0.01" min="0.01" placeholder="0,00"
                className={`${base} w-28 text-right ${isManual ? "border-amber-500/70 ring-1 ring-amber-500/40" : ""} ${vals[i] <= 0 ? "border-rose-500 ring-1 ring-rose-500/50" : ""}`}
                value={isManual ? parc.manual[i] : (total ? vals[i].toFixed(2) : "")} onChange={(e) => setManual(i, e.target.value)} />
              <DatePicker value={d} onChange={(v) => setDue(i, v)} testId={`avulso-parcela-due-${i + 1}`} placeholder="Vencimento" />
            </div>
          );
        })}
        {total > 0 && (
          <p data-testid="avulso-parcelas-soma" className={`text-xs font-medium pt-1 border-t border-zinc-800 ${ok ? "text-emerald-400" : "text-rose-400"}`}>
            {ok
              ? `✓ Soma das parcelas: ${fmtBRL(soma)} — bate com o valor total`
              : vals.some((v) => v <= 0)
                ? `✕ Há parcela zerada ou negativa — os valores editados excedem o total de ${fmtBRL(total)}`
                : `✕ Soma das parcelas: ${fmtBRL(soma)} — difere do valor total (${fmtBRL(total)})`}
          </p>
        )}
      </div>
    </div>
  );
};
